import { FileText, Plus } from '@gravity-ui/icons'
import { Button, EmptyState, Table } from '@heroui/react'
import type { Control, UseFormRegister } from 'react-hook-form'
import { useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { calcPLN, formatPLN } from '@/utils/currency'

import { ExpenseTableRow } from './ExpenseTableRow'

interface ExpenseTableProps {
  fields: any[];
  rows: any[];
  register: UseFormRegister<any>;
  control: Control<any>;
  setValue: (name: string, value: any, options?: any) => void;
  onRemove: (index: number) => void;
  onFetchRate: (index: number, currency: string, date: string) => void;
  onAdd: () => void;
}

export function ExpenseTable({
                               fields,
                               rows,
                               register,
                               control,
                               setValue,
                               onRemove,
                               onFetchRate,
                               onAdd,
                             }: ExpenseTableProps) {
  const { t } = useTranslation()

  const watchedRows = useWatch({
    control,
    name: 'rows',
    defaultValue: rows,
  })

  const grossTotal = (watchedRows || []).reduce((s: number, r: any) => s + calcPLN(Number(r?.amount) || 0, Number(r?.exchangeRate) || 0), 0)
  const netTotal = (watchedRows || []).filter((r: any) => r?.isIncluded !== false).reduce((s: number, r: any) => s + calcPLN(Number(r?.amount) || 0, Number(r?.exchangeRate) || 0), 0)

  return (
    <div className="overflow-x-auto">
      <Table>
        <Table.ScrollContainer>
          <Table.Content aria-label={t('reports.form.rows_title')} className="min-w-[1000px]">
            <Table.Header>
              <Table.Column style={{ width: 60 }} isRowHeader>{t('reports.table.include', 'Вкл.')}</Table.Column>
              <Table.Column style={{ minWidth: 130 }}>{t('common.date')}</Table.Column>
              <Table.Column style={{ minWidth: 180 }}>{t('reports.table.type')}</Table.Column>
              <Table.Column style={{ minWidth: 120 }}>{t('reports.table.doc')}</Table.Column>
              <Table.Column style={{ width: 80 }}>{t('reports.table.country')}</Table.Column>
              <Table.Column style={{ width: 100 }}>{t('reports.table.amount')}</Table.Column>
              <Table.Column style={{ width: 90 }}>{t('reports.table.currency')}</Table.Column>
              <Table.Column style={{ width: 90 }}>{t('reports.table.rate')}</Table.Column>
              <Table.Column style={{ width: 110 }}>{t('reports.table.pln')}</Table.Column>
              <Table.Column style={{ width: 50 }}> </Table.Column>
            </Table.Header>
            <Table.Body
              renderEmptyState={() => (
                <EmptyState className="flex h-30 mt-6 w-full flex-col items-center justify-center gap-4 text-center">
                  <FileText className="size-6 text-zinc-400" />
                  <span className="text-sm text-zinc-500">{t('common.no_results', 'No results found')}</span>
                </EmptyState>
              )}
            >
              {fields.map((field, i) => (
                <ExpenseTableRow
                  key={field.id}
                  index={i}
                  fieldId={field.id}
                  register={register}
                  control={control}
                  setValue={setValue}
                  row={rows[i]}
                  onRemove={onRemove}
                  onFetchRate={onFetchRate}
                />
              ))}
              <Table.Row key="add-new-row-btn">
                <Table.Cell colSpan={10} className="">
                  <Button
                    variant="ghost"
                    className="w-full text-zinc-500 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-100 hover:bg-zinc-100 dark:hover:bg-zinc-800"
                    onClick={onAdd}
                  >
                    <Plus className="size-4 mr-2" />
                    {t('reports.form.add_row')}
                  </Button>
                </Table.Cell>
              </Table.Row>
            </Table.Body>
          </Table.Content>
        </Table.ScrollContainer>
        <Table.Footer>
          <div className="flex w-full gap-6 py-2 justify-end">
            <div className="text-right">
              <div
                className="text-xs font-black text-zinc-600 dark:text-zinc-400"
              >{t('reports.form.total_net', 'Итого (без исключённых)')}</div>
              <div className="text-md font-bold tracking-tight text-zinc-500 dark:text-zinc-400 mt-1">
                {formatPLN(netTotal)} PLN
              </div>
            </div>
            <div className="text-right">
              <div
                className="text-xs font-black text-zinc-600 dark:text-zinc-400"
              >{t('reports.form.total_payout')}</div>
              <div className="text-lg font-black tracking-tight">
                {formatPLN(grossTotal)} PLN
              </div>
            </div>
          </div>
        </Table.Footer>
      </Table>
    </div>
  )
}
